import { useState } from 'react';

interface Step {
  id: number;
  label: string;
  week: string;
  color: string;
}

interface Props {
  steps: Step[];
  activeStep: number;
  onStepClick: (id: number) => void;
}

const PROCESS_DETAILS = [
  {
    id: 1,
    title: '철거 공정',
    period: '1주차 (1~5일)',
    description:
      '기존 인테리어를 완전히 제거하는 단계입니다. 벽체, 바닥재, 천장재, 기존 가구 및 설비를 철거하고 폐기물을 처리합니다. 구조 상태를 점검하고 다음 공정을 위한 기초 작업을 진행합니다.',
    tasks: ['기존 바닥재 철거', '벽체 및 천장 철거', '기존 설비 제거', '폐기물 반출 처리', '구조 상태 점검'],
    tip: '철거 전 반드시 관리사무소 신고 및 이웃 공지가 필요합니다. 소음 작업은 오전 9시~오후 5시 사이에 진행됩니다.',
    images: [
      {
        url: 'https://public.readdy.ai/ai/img_res/f161f97a353eef320c941b8f01d93df4.jpg',
        caption: '바닥재 및 벽체 철거 작업',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=apartment%20demolition%20interior%20renovation%20bare%20concrete%20walls%20ceiling%20exposed%20structure%20construction%20site%20realistic%20photography%20professional&width=600&height=400&seq=timeline-demo-02&orientation=landscape',
        caption: '철거 완료 후 구조 점검',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=construction%20waste%20debris%20removal%20renovation%20site%20workers%20cleaning%20rubble%20professional%20photography%20realistic%20interior%20demolition&width=600&height=400&seq=timeline-demo-03&orientation=landscape',
        caption: '폐기물 반출 및 현장 정리',
      },
    ],
  },
  {
    id: 2,
    title: '설비 / 배관 공정',
    period: '1~2주차 (4~8일)',
    description:
      '수도, 난방, 환기 등 기반 설비를 새롭게 배치하는 단계입니다. 욕실 및 주방 배관 위치를 확정하고, 난방 배관(온돌) 교체 및 보일러 설치를 진행합니다.',
    tasks: ['급수/배수 배관 교체', '난방 배관(온돌) 시공', '보일러 교체 및 설치', '욕실 방수 처리', '환기 덕트 설치'],
    tip: '배관 공사는 이후 공정에 영향을 미치므로 꼼꼼한 누수 테스트가 필수입니다. 방수 작업 후 최소 24시간 양생이 필요합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=plumbing%20pipe%20installation%20floor%20heating%20system%20ondol%20construction%20site%20apartment%20renovation%20professional%20photography%20realistic%20pipes%20concrete%20floor&width=600&height=400&seq=timeline-plumb-01&orientation=landscape',
        caption: '온돌 난방 배관 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=bathroom%20waterproofing%20construction%20site%20renovation%20professional%20worker%20applying%20waterproof%20coating%20realistic%20photography%20interior&width=600&height=400&seq=timeline-plumb-02&orientation=landscape',
        caption: '욕실 방수 처리 작업',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=boiler%20installation%20gas%20heating%20system%20apartment%20renovation%20professional%20plumber%20worker%20realistic%20photography%20construction%20site&width=600&height=400&seq=timeline-plumb-03&orientation=landscape',
        caption: '보일러 설치 및 배관 연결',
      },
    ],
  },
  {
    id: 3,
    title: '목공 · 창호 공정',
    period: '2~3주차 (6~14일)',
    description:
      '공간의 뼈대를 만드는 핵심 공정입니다. 벽체 구성, 천장 틀 작업, 붙박이장 및 수납공간 제작, 문틀 설치와 함께 창호(시스템창, 현관문, 실내문) 교체를 이 단계에서 함께 진행합니다. 창호는 단열·방음 성능과 인테리어 완성도를 동시에 결정하는 중요한 공정입니다.',
    tasks: [
      '경량 칸막이 벽체 시공',
      '천장 석고보드 작업',
      '붙박이장 제작 설치',
      '문틀 및 문짝 설치',
      '몰딩 및 마감 틀 작업',
      '시스템 창호 교체',
      '현관문 교체',
      '실내 중문 설치',
    ],
    tip: '창호 교체는 반드시 관리사무소 사전 신고 후 진행해야 합니다. 시스템창 발주 후 제작 기간(약 7~10일)이 필요하므로 공정 시작 전 미리 주문하세요. 목공 단계에서 콘센트·스위치 위치도 전기 기사와 함께 확정해야 합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=carpentry%20framing%20gypsum%20board%20ceiling%20installation%20apartment%20renovation%20construction%20site%20professional%20worker%20realistic%20photography%20interior%20wood%20frame&width=600&height=400&seq=timeline-carp-01&orientation=landscape',
        caption: '천장 석고보드 틀 작업',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=modern%20system%20window%20installation%20Korean%20apartment%20renovation%20aluminum%20frame%20double%20glazed%20professional%20worker%20installing%20window%20frame%20construction%20site%20realistic%20photography&width=600&height=400&seq=timeline-carp-win01&orientation=landscape',
        caption: '시스템 창호 교체 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=modern%20front%20door%20installation%20apartment%20renovation%20professional%20worker%20installing%20premium%20entrance%20door%20steel%20frame%20realistic%20photography%20construction%20site%20interior&width=600&height=400&seq=timeline-carp-win02&orientation=landscape',
        caption: '현관문 및 실내 중문 설치',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=built-in%20wardrobe%20carpentry%20installation%20apartment%20renovation%20construction%20site%20professional%20worker%20wood%20frame%20realistic%20photography%20interior&width=600&height=400&seq=timeline-carp-02&orientation=landscape',
        caption: '붙박이장 목공 제작',
      },
    ],
  },
  {
    id: 4,
    title: '전기 / 조명 공정',
    period: '3주차 (11~15일)',
    description:
      '전기 배선 및 조명 설치 단계입니다. 분전반 교체, 콘센트 및 스위치 배치, 조명 배선 작업을 진행합니다. 스마트홈 시스템 연동도 이 단계에서 함께 진행됩니다.',
    tasks: ['분전반 교체 및 배선', '콘센트/스위치 설치', '조명 배선 및 기구 설치', '에어컨 배선 작업', '스마트홈 시스템 연동'],
    tip: '조명 계획은 공간의 분위기를 결정하는 중요한 요소입니다. 간접조명, 포인트 조명, 기능 조명을 적절히 조합하는 것을 권장합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=electrical%20wiring%20installation%20apartment%20renovation%20construction%20site%20electrician%20worker%20conduit%20pipes%20ceiling%20realistic%20photography%20professional&width=600&height=400&seq=timeline-elec-01&orientation=landscape',
        caption: '전기 배선 및 전선관 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=recessed%20LED%20lighting%20installation%20ceiling%20apartment%20renovation%20construction%20site%20electrician%20worker%20realistic%20photography%20professional%20interior&width=600&height=400&seq=timeline-elec-02&orientation=landscape',
        caption: '매입형 LED 조명 설치',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=electrical%20panel%20distribution%20board%20installation%20apartment%20renovation%20electrician%20worker%20realistic%20photography%20professional%20construction%20site&width=600&height=400&seq=timeline-elec-03&orientation=landscape',
        caption: '분전반 교체 및 회로 구성',
      },
    ],
  },
  {
    id: 5,
    title: '타일 / 바닥 공정',
    period: '3~4주차 (13~18일)',
    description:
      '욕실, 주방 타일 시공과 거실·방 바닥재 시공을 진행합니다. 타일 줄눈 작업, 마루 또는 강마루 시공, 현관 타일 작업이 포함됩니다.',
    tasks: ['욕실 벽/바닥 타일 시공', '주방 타일 시공', '거실/방 마루 시공', '현관 타일 시공', '줄눈 및 실리콘 마감'],
    tip: '타일 시공 후 줄눈 양생에 최소 24~48시간이 필요합니다. 마루 시공 전 바닥 평탄화 작업이 선행되어야 합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=bathroom%20tile%20installation%20worker%20laying%20large%20format%20porcelain%20tiles%20wall%20floor%20apartment%20renovation%20realistic%20photography%20professional%20construction&width=600&height=400&seq=timeline-tile-01&orientation=landscape',
        caption: '욕실 대형 포세린 타일 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=hardwood%20floor%20installation%20worker%20laying%20engineered%20wood%20flooring%20apartment%20renovation%20realistic%20photography%20professional%20construction%20site%20interior&width=600&height=400&seq=timeline-tile-02&orientation=landscape',
        caption: '거실 강마루 바닥재 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=kitchen%20herringbone%20tile%20backsplash%20installation%20worker%20apartment%20renovation%20realistic%20photography%20professional%20construction%20site%20interior%20mosaic&width=600&height=400&seq=timeline-tile-03&orientation=landscape',
        caption: '주방 헤링본 타일 시공',
      },
    ],
  },
  {
    id: 6,
    title: '도장 / 도배 공정',
    period: '4주차 (16~20일)',
    description:
      '벽면과 천장의 마감 작업입니다. 친환경 페인트 도장 또는 벽지 시공을 진행하며, 공간의 색감과 분위기를 완성합니다. 도장과 도배의 조합으로 다양한 스타일 연출이 가능합니다.',
    tasks: ['퍼티 및 샌딩 작업', '천장 도장 시공', '벽면 도장 또는 도배', '포인트 벽 시공', '문짝 도장 마감'],
    tip: '친환경 저VOC 페인트 사용을 권장합니다. 도장 후 충분한 환기(최소 3~5일)가 필요하며, 입주 전 냄새 제거가 중요합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=interior%20painting%20worker%20rolling%20white%20paint%20on%20wall%20apartment%20renovation%20professional%20painter%20realistic%20photography%20construction%20site%20clean%20finish&width=600&height=400&seq=timeline-paint-01&orientation=landscape',
        caption: '벽면 친환경 페인트 도장',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=wallpaper%20installation%20worker%20hanging%20wallpaper%20apartment%20renovation%20professional%20realistic%20photography%20construction%20site%20interior%20decorator&width=600&height=400&seq=timeline-paint-02&orientation=landscape',
        caption: '실크 벽지 도배 시공',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=accent%20wall%20painting%20dark%20color%20feature%20wall%20apartment%20renovation%20professional%20painter%20realistic%20photography%20construction%20site%20interior%20design&width=600&height=400&seq=timeline-paint-03&orientation=landscape',
        caption: '포인트 벽 도장 마감',
      },
    ],
  },
  {
    id: 7,
    title: '가구 / 마감 공정',
    period: '4~5주차 (18~25일)',
    description:
      '인테리어의 완성 단계입니다. 주방 가구(싱크대, 상부장), 욕실 기기 설치, 조명 기구 최종 설치, 도어락 및 하드웨어 설치를 진행합니다. 청소 및 최종 점검으로 마무리합니다.',
    tasks: ['주방 싱크대 및 상부장 설치', '욕실 위생기기 설치', '조명 기구 최종 설치', '도어락 및 하드웨어', '입주 청소 및 최종 점검'],
    tip: '가구 설치 전 반드시 치수를 재확인하세요. 냉장고, 세탁기 등 대형 가전의 반입 동선도 미리 확인이 필요합니다.',
    images: [
      {
        url: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20cabinet%20installation%20worker%20fitting%20white%20kitchen%20furniture%20apartment%20renovation%20professional%20realistic%20photography%20construction%20site%20interior%20finished&width=600&height=400&seq=timeline-furn-01&orientation=landscape',
        caption: '주방 싱크대 및 상부장 설치',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=bathroom%20fixture%20installation%20toilet%20vanity%20sink%20apartment%20renovation%20professional%20worker%20realistic%20photography%20construction%20site%20interior%20finished%20clean&width=600&height=400&seq=timeline-furn-02&orientation=landscape',
        caption: '욕실 위생기기 최종 설치',
      },
      {
        url: 'https://readdy.ai/api/search-image?query=completed%20modern%20apartment%20interior%20renovation%20finished%20living%20room%20clean%20minimalist%20design%20professional%20photography%20elegant%20warm%20lighting%20beautiful&width=600&height=400&seq=timeline-furn-03&orientation=landscape',
        caption: '인테리어 완공 후 최종 모습',
      },
    ],
  },
];

export default function ProcessTimeline({ steps, activeStep, onStepClick }: Props) {
  const [activeImg, setActiveImg] = useState(0);
  const detail = PROCESS_DETAILS.find((d) => d.id === activeStep)!;

  // Reset image index when step changes
  const handleStepClick = (id: number) => {
    setActiveImg(0);
    onStepClick(id);
  };

  return (
    <div>
      {/* Section Header */}
      <div className="text-center mb-12">
        <span
          className="inline-block text-xs font-semibold tracking-[0.3em] uppercase mb-3"
          style={{ color: '#c9a84c' }}
        >
          Process Schedule
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-[#2a2218] mb-3">
          30평 기준 인테리어 공정표
        </h2>
        <p className="text-[#8a7a6a] text-sm">총 4~5주 소요 · 공정을 클릭하면 상세 내용을 확인할 수 있습니다</p>
      </div>

      {/* Timeline Bar */}
      <div className="relative mb-10">
        <div className="grid grid-cols-5 mb-3 px-2">
          {['1주차', '2주차', '3주차', '4주차', '5주차'].map((w) => (
            <div key={w} className="text-center text-xs text-[#8a7a6a] font-medium">
              {w}
            </div>
          ))}
        </div>

        <div className="bg-[#eeece9] rounded-xl p-4 space-y-3">
          {steps.map((step) => {
            const ganttMap: Record<number, { start: number; span: number }> = {
              1: { start: 0, span: 1 },
              2: { start: 0.6, span: 1.4 },
              3: { start: 1.2, span: 1.8 },
              4: { start: 2.2, span: 1 },
              5: { start: 2.6, span: 1.4 },
              6: { start: 3.2, span: 1 },
              7: { start: 3.6, span: 1.4 },
            };
            const g = ganttMap[step.id];
            const leftPct = (g.start / 5) * 100;
            const widthPct = (g.span / 5) * 100;
            const isActive = activeStep === step.id;

            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                className="w-full flex items-center gap-3 cursor-pointer group"
              >
                <span
                  className={`text-xs font-medium w-20 text-center shrink-0 transition-colors ${
                    isActive ? 'text-[#c9a84c]' : 'text-[#8a7a6a] group-hover:text-[#3a3228]'
                  }`}
                >
                  {step.label}
                </span>
                <div className="flex-1 relative h-7">
                  <div
                    className="absolute top-0 h-full rounded-full flex items-center px-3 transition-all duration-300"
                    style={{
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      backgroundColor: isActive ? step.color : `${step.color}55`,
                      border: isActive ? `1.5px solid ${step.color}` : '1.5px solid transparent',
                    }}
                  >
                    <span
                      className={`text-xs font-semibold whitespace-nowrap transition-colors ${
                        isActive ? 'text-[#0d0d0d]' : 'text-[#3a3228]/70'
                      }`}
                    >
                      {step.week}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail Card */}
      <div
        className="rounded-2xl overflow-hidden border transition-all duration-300"
        style={{ backgroundColor: '#ffffff', borderColor: '#c9a84c22' }}
      >
        <div className="flex flex-col lg:flex-row">

          {/* LEFT — Photo Gallery */}
          <div className="lg:w-[480px] shrink-0 flex flex-col">
            <div className="relative w-full h-64 lg:h-80 overflow-hidden bg-[#eeece9]">
              <img
                key={detail.images[activeImg].url}
                src={detail.images[activeImg].url}
                alt={detail.images[activeImg].caption}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-[#0d0d0d]"
                  style={{ backgroundColor: '#c9a84c' }}
                >
                  {detail.id}
                </span>
                <span className="text-white text-xs font-semibold bg-black/40 px-2 py-1 rounded-full backdrop-blur-sm">
                  {detail.title}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white/90 text-xs font-medium">{detail.images[activeImg].caption}</p>
              </div>
              {detail.images.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImg((prev) => (prev - 1 + detail.images.length) % detail.images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white cursor-pointer hover:bg-black/70 transition-colors backdrop-blur-sm"
                  >
                    <i className="ri-arrow-left-s-line text-base" />
                  </button>
                  <button
                    onClick={() => setActiveImg((prev) => (prev + 1) % detail.images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white cursor-pointer hover:bg-black/70 transition-colors backdrop-blur-sm"
                  >
                    <i className="ri-arrow-right-s-line text-base" />
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2 p-3 bg-[#f4f2f0]">
              {detail.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className="relative flex-1 h-16 rounded-lg overflow-hidden cursor-pointer transition-all duration-200"
                  style={{
                    border: activeImg === idx ? '2px solid #c9a84c' : '2px solid transparent',
                    opacity: activeImg === idx ? 1 : 0.55,
                  }}
                >
                  <img
                    src={img.url}
                    alt={img.caption}
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              ))}
            </div>
            <div className="flex items-center justify-center gap-1.5 pb-3 bg-[#f4f2f0]">
              {detail.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className="rounded-full transition-all duration-200 cursor-pointer"
                  style={{
                    width: activeImg === idx ? '16px' : '6px',
                    height: '6px',
                    backgroundColor: activeImg === idx ? '#c9a84c' : '#c9a84c44',
                  }}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — Info */}
          <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
            <div>
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-xl font-bold text-[#2a2218] mb-1">{detail.title}</h3>
                <span className="text-xs text-[#8a7a6a]">{detail.period}</span>
              </div>

              {/* Description */}
              <p className="text-[#6b5e4e] text-sm leading-relaxed mb-6">{detail.description}</p>

              {/* Tasks */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-[#8a7a6a] uppercase tracking-widest mb-3">주요 작업 항목</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detail.tasks.map((task) => (
                    <div key={task} className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-xs" style={{ color: '#c9a84c' }} />
                      </span>
                      <span className="text-[#3a3228] text-sm">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tip Box */}
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: '#c9a84c0d', border: '1px solid #c9a84c33' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-lightbulb-flash-line text-sm" style={{ color: '#c9a84c' }} />
                <span className="text-xs font-semibold" style={{ color: '#c9a84c' }}>
                  전문가 TIP
                </span>
              </div>
              <p className="text-[#6b5e4e] text-xs leading-relaxed">{detail.tip}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
